import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import SVG from 'react-inlinesvg';
import classNames from 'classnames/bind';
import Auth from 'modules/Auth';
import { formatMarket } from 'utils/formatters';
import globeSvg from 'assets/icons/globe.svg';
import arrowLeftSvg from 'assets/icons/arrow_left.svg';

import Button, { Color } from 'lib/Button';
import IconButton from 'lib/IconButton';
import { Theme } from 'lib/IconButton/IconButton.types';
import { useHasFeatureAccess } from 'customHooks/useHasFeatureAccess';
import { FeatureFlags } from 'components/common/types/Features.types';
import styles from './Header.pcss';
import { Store } from '../types/Store.types';
import { HeaderProps } from './Header.types';
import Timezone from './SelectTimezone';

const cx = classNames.bind(styles);

const Header: React.FC<HeaderProps> = ({ title = '', actionButton, subNavigation, onBackClick }) => {
  const navigate = useNavigate();
  const environmentId = useSelector((state: Store) => state.environment.environmentId);
  const hasDirectSalesAccess = useHasFeatureAccess(FeatureFlags.DIRECT_SALES_PLANNER);
  const { markets } = Auth.getData();
  const allowChangeEnv = markets?.length > 1;

  return (
    <header className="sticky top-0 h-15 px-4 bg-neutral-100 z-20 border-b border-b-neutral-300">
      <div className="row h-full">
        <div className="px-4 flex items-center">
          {onBackClick && (
            <span className="mr-4">
              <IconButton
                icon={arrowLeftSvg}
                dataTestId="back-click"
                handleOnClick={onBackClick}
                name="back-click"
                theme={Theme.PRIMARY}
              />
            </span>
          )}
          <h2
            data-test-id="page-title"
            title={title}
            className="mr-4 header-xl text-neutral-900 text-ellipsis overflow-hidden whitespace-nowrap"
          >
            {title}
          </h2>
        </div>
        <div className="self-end">{subNavigation}</div>
        <div className="flex ml-auto text-right self-center">
          <div className="mr-5 space-x-5 flex items-center">{actionButton}</div>
          {!hasDirectSalesAccess && (
            <div className={cx('mr-5 mt-[1px]', 'timezone-container')}>
              <Timezone />
            </div>
          )}

          <div className="mr-5 align-middle inline-block">
            <Button
              dataTestId="environment-selection"
              onClick={() => {
                navigate('/landing');
              }}
              classNames="!rounded-full !bg-transparent"
              color={Color.SECONDARY}
              isDisabled={!allowChangeEnv}
            >
              <div className="mx-2 flex gap-2 justify-center items-center">
                <SVG className="m-0 text-neutral-500" src={globeSvg} />
                <span className="text-neutral-500">{formatMarket(environmentId)}</span>
              </div>
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
